import type { GlobalContactInfo } from '../pages/website-builder/page';
import { type PageContent, type ThemeData, type ContentSection, type SiteNode } from '@/mocks/websiteBuilder';
import { normalizeCustomSection } from './customSectionUtils';

export function parseJSONSafely(raw: any, defaultFallback: any) {
  if (typeof raw === 'object' && raw !== null) return raw;
  if (typeof raw !== 'string') return defaultFallback;
  try {
    const res = JSON.parse(raw);
    return typeof res === 'object' && res !== null ? res : defaultFallback;
  } catch {
    const fallback = { ...defaultFallback };
    const keys = Object.keys(fallback);
    let foundAny = false;
    for (const key of keys) {
      const regex = new RegExp(`"${key}"\\s*:\\s*"([^"]*)"`);
      const match = raw.match(regex);
      if (match) {
        fallback[key] = match[1];
        foundAny = true;
      }
    }
    if (foundAny) return fallback;

    const mainKey = Object.keys(fallback).find(k => k === 'title' || k === 'name' || k === 'headline' || k === 'text') || 'title';
    fallback[mainKey] = raw;
    return fallback;
  }
}

function parseHeroSlide(raw: any) {
  try {
    if (typeof raw === 'string') return JSON.parse(raw) as { headline: string; sub: string; cta: string; image: string };
    return raw as { headline: string; sub: string; cta: string; image: string };
  } catch {
    return { headline: typeof raw === 'string' ? raw : '', sub: '', cta: 'Learn More', image: '' };
  }
}

export function shouldRenderSection(section: ContentSection, allPages?: PageContent[], pageId?: string): boolean {
  if (section.type === 'contactForm') {
    return pageId === 'contact' || !!(typeof section.content === 'string' ? section.content.trim() : section.content);
  }
  if (section.type === 'contactInfo' || section.type === 'contact') {
    if (typeof section.content === 'object' && section.content !== null) {
      return Object.values(section.content).some(v => v && String(v).trim().length > 0);
    }
    if (typeof section.content === 'string' && section.content.trim()) {
      try {
        const parsed = JSON.parse(section.content);
        return Object.values(parsed).some(v => v && String(v).trim().length > 0);
      } catch {
        return section.content.trim().length > 0;
      }
    }
    return pageId === 'contact';
  }
  if (section.type === 'story' || section.type === 'text') {
    const text = typeof section.content === 'string' ? section.content : Array.isArray(section.content) ? section.content.join(' ') : '';
    const plainText = text.replace(/<[^>]*>?/gm, '').trim();
    return plainText.length > 0;
  }
  if (section.type === 'custom' || section.type === 'customSection') {
    const payload = normalizeCustomSection(section.content);
    return payload.blocks.some(b => {
      const plain = (b.body || '').replace(/<[^>]*>?/gm, '').trim();
      return plain.length > 0 || (b.title && b.title.trim().length > 0) || (b.image && b.image.trim().length > 0);
    });
  }

  let items = Array.isArray(section.content) ? section.content : [];

  if (pageId === 'home') {
    if (section.type === 'gallery') {
      const galleryPage = allPages?.find(p => p.pageId === 'gallery');
      const galleryPageSection = galleryPage?.sections.find(s => s.type === 'gallery');
      const galleryItems = Array.isArray(galleryPageSection?.content) ? galleryPageSection.content : [];
      if (galleryItems.length === 0) return false;
      items = galleryItems.slice(0, 3);
    } else if (section.type === 'blog') {
      const blogPage = allPages?.find(p => p.pageId === 'blog');
      const blogPageSection = blogPage?.sections.find(s => s.type === 'blog');
      const blogItems = Array.isArray(blogPageSection?.content) ? blogPageSection.content : [];
      if (blogItems.length < 3) return false;
      items = blogItems.slice(0, 3);
    }
  }

  if (items.length === 0) return false;

  switch (section.type) {
    case 'hero': {
      const parsed = items.map(i => parseHeroSlide(i));
      return parsed.some(s => (s.headline && s.headline.trim().length > 0) || (s.sub && s.sub.trim().length > 0) || (s.image && !s.image.includes('placeholder') && s.image.trim().length > 0));
    }
    case 'gallery': {
      const parsed = items.map(item => parseJSONSafely(item, { src: typeof item === 'string' ? item : '', type: 'image' }));
      const validImages = parsed.filter(i => i.src && !i.src.includes('placeholder') && i.src.trim().length > 0);
      return validImages.length > 0;
    }
    case 'founder':
    case 'team': {
      const parsed = items.map(m => parseJSONSafely(m, { name: typeof m === 'string' ? m : '', role: '', bio: '', image: '' }));
      return parsed.some(f => (f.name && f.name.trim().length > 0) || (f.bio && f.bio.trim().length > 0) || (f.image && !f.image.includes('placeholder') && f.image.trim().length > 0));
    }
    case 'testimonial': {
      const parsed = items.map(i => parseJSONSafely(i, { quote: typeof i === 'string' ? i : '', client: '', role: '' }));
      return parsed.some(t => (t.quote && !t.quote.includes('dummy') && t.quote.trim().length > 0) || (t.client && t.client.trim().length > 0));
    }
    case 'grid':
    case 'usp':
    case 'serviceCard':
    case 'productCard': {
      const parsed = items.map(i => parseJSONSafely(i, { title: typeof i === 'string' ? i : '', name: typeof i === 'string' ? i : '', description: '', image: '' }));
      return parsed.some(c => (c.title && c.title.trim().length > 0) || (c.name && c.name.trim().length > 0) || (c.description && c.description.trim().length > 0));
    }
    case 'clientLogos': {
      const parsed = items.map(i => parseJSONSafely(i, { name: typeof i === 'string' ? i : '', logo: '' }));
      return parsed.some(l => (l.logo && !l.logo.includes('placeholder') && l.logo.trim().length > 0) || (l.name && l.name.trim().length > 0));
    }
    case 'faq': {
      const parsed = items.map(i => parseJSONSafely(i, { question: typeof i === 'string' ? i : '', answer: '' }));
      return parsed.some(f => (f.question && f.question.trim().length > 0) || (f.answer && f.answer.trim().length > 0));
    }
    case 'blog': {
      const parsed = items.map(p => parseJSONSafely(p, { title: typeof p === 'string' ? p : '', excerpt: '', date: '', content: '' }));
      return parsed.some(p => p.title && p.title.trim().length > 0);
    }
    default:
      return true;
  }
}

function generateExcerpt(html: string): string {
  const text = html.replace(/<[^>]*>?/gm, '').trim();
  return text.length > 180 ? text.substring(0, 180) + '...' : text;
}

export function isPageGeneratedOrHasContent(page: PageContent, allPages: PageContent[] = []): boolean {
  if (!page || !page.sections || !Array.isArray(page.sections)) return false;
  if (page.status === 'Generated' || page.status === 'Edited') return true;

  // If status is Pending:
  if (page.pageId === 'home') {
    // For Home page, check if home has generated hero or other core sections of its own (excluding auto-injected cross-page items)
    const homeCoreSections = page.sections.filter(s => s.type !== 'gallery' && s.type !== 'blog');
    return homeCoreSections.some(s => s.enabled !== false && shouldRenderSection(s, allPages, page.pageId));
  }

  return page.sections.some(s => s.enabled !== false && shouldRenderSection(s, allPages, page.pageId));
}

export function determineInitialPageId(pages: PageContent[], initialPageId?: string): string {
  if (!pages || pages.length === 0) return initialPageId || 'home';

  // 1. If initialPageId was specified and has generated content
  if (initialPageId) {
    const requestedPage = pages.find(p => p.pageId === initialPageId);
    if (requestedPage && isPageGeneratedOrHasContent(requestedPage, pages)) {
      return requestedPage.pageId;
    }
  }

  // 2. If 'home' is generated and no conflicting initialPageId was requested (or initial was 'home')
  const homePage = pages.find(p => p.pageId === 'home');
  if (homePage && isPageGeneratedOrHasContent(homePage, pages) && (!initialPageId || initialPageId === 'home')) {
    return 'home';
  }

  // 3. Find the first page in pages that has generated content
  const firstGeneratedPage = pages.find(p => isPageGeneratedOrHasContent(p, pages));
  if (firstGeneratedPage) {
    return firstGeneratedPage.pageId;
  }

  // 4. Fallbacks if no page has been generated yet
  if (initialPageId && pages.some(p => p.pageId === initialPageId)) {
    return initialPageId;
  }
  if (homePage) {
    return 'home';
  }
  return pages[0]?.pageId || 'home';
}

export function generatePageHTML(
  page: PageContent,
  theme: ThemeData,
  allPages: PageContent[],
  brandName: string,
  brandLogo: string | null = null,
  globalContactInfo: GlobalContactInfo | null = null,
  isPreview: boolean = false
): string {

  const navPages = allPages.map(p => ({ id: p.pageId, name: p.pageName, enabled: true }));
  const sectionsCopy = [...page.sections];
  if (page.pageId === 'home') {
    if (!sectionsCopy.some(s => s.type === 'gallery')) {
      sectionsCopy.push({ id: 'gallery-auto', title: 'Gallery Strip', type: 'gallery', content: [], editing: false, enabled: true });
    }
    if (!sectionsCopy.some(s => s.type === 'blog')) {
      sectionsCopy.push({ id: 'blog-auto', title: 'Latest Blogs', type: 'blog', content: [], editing: false, enabled: true });
    }
  }
  const enabledSections = sectionsCopy.filter(s => s.enabled !== false && shouldRenderSection(s, allPages, page.pageId));

  const hasSubstantialContent = isPageGeneratedOrHasContent(page, allPages) && enabledSections.length > 0;

  if (!hasSubstantialContent) {
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${page.pageName} | ${brandName}</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
  <link href="https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css" rel="stylesheet">
  <style>
    body { font-family: 'Inter', sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; margin: 0; background-color: #f9fafb; color: #6b7280; text-align: center; padding: 20px; }
    .empty-state { max-width: 440px; background: #ffffff; padding: 40px 32px; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.05); border: 1px solid #e5e7eb; }
    h2 { color: #111827; margin-bottom: 8px; font-weight: 600; font-size: 20px; }
    p { margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: #6b7280; }
    .btn-group { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
    .btn-secondary { padding: 10px 18px; background: #fff; border: 1px solid #d1d5db; border-radius: 8px; cursor: pointer; color: #374151; font-weight: 500; display: inline-flex; align-items: center; gap: 6px; font-size: 14px; transition: all 0.15s ease; text-decoration: none; }
    .btn-secondary:hover { background: #f3f4f6; border-color: #9ca3af; }
    .btn-primary { padding: 10px 20px; background: ${theme.cta || '#0859b8'}; color: #fff; border: none; border-radius: 8px; cursor: pointer; font-weight: 500; display: inline-flex; align-items: center; gap: 6px; font-size: 14px; transition: all 0.15s ease; text-decoration: none; }
    .btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }
  </style>
</head>
<body>
  <div class="empty-state">
    <div style="width: 64px; height: 64px; background: #f3f4f6; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;">
      <i class="ri-file-search-line" style="font-size: 32px; color: #9ca3af;"></i>
    </div>
    <h2>No content yet</h2>
    <p>The <strong>${page.pageName}</strong> page hasn't been generated yet or has empty sections. Go to the editor and click 'Generate Page' to build its content.</p>
    ${isPreview ? `
      <div class="btn-group">
        <button onclick="window.parent.postMessage({ type: 'CLOSE_PREVIEW' }, '*')" class="btn-primary">
          <i class="ri-arrow-left-line"></i> Go Back to Builder
        </button>
        <button onclick="window.parent.postMessage({ type: 'GO_BACK' }, '*')" class="btn-secondary">
          <i class="ri-history-line"></i> Previous Page
        </button>
      </div>
    ` : ''}
  </div>
</body>
</html>`;
  }

  let body = '';

  for (const section of enabledSections) {
    try {
      body += generateSectionHTML(section, theme, allPages, globalContactInfo, isPreview, page.pageId);
    } catch (err) {
      console.error(`Failed to generate section ${section.type}:`, err);
      if (isPreview) {
        body += `<div style="padding: 20px; border: 1px dashed red; color: red; text-align: center;">Error rendering ${section.type} section</div>`;
      }
    }
  }

  const navHTML = generateNavHTML(navPages, theme, brandName, brandLogo, isPreview);
  const footerHTML = generateFooterHTML(navPages, theme, brandName, brandLogo, globalContactInfo, isPreview);

  const previewScript = isPreview ? `
  <script>
    document.addEventListener('click', function(e) {
      const a = e.target.closest('a');
      if (a && a.getAttribute('data-nav-id')) {
        e.preventDefault();
        window.parent.postMessage({ type: 'NAVIGATE', pageId: a.getAttribute('data-nav-id') }, '*');
      }
    });
  </script>` : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${page.pageName} | ${brandName}</title>
  <meta name="description" content="Draft content and SEO foundation for the ${page.pageName} page. Auto-generated by ${brandName} website builder.">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=${encodeURIComponent(theme.headingFont.replace(' ', '+'))}:wght@400;700&family=${encodeURIComponent(theme.bodyFont.replace(' ', '+'))}:wght@400;500;700&display=swap" rel="stylesheet">
  <link href="https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css" rel="stylesheet">
  <style>
    :root {
      --primary: ${theme.primary};
      --accent: ${theme.accent};
      --bg: ${theme.background};
      --text: ${theme.text};
      --cta: ${theme.cta};
      --heading: '${theme.headingFont}', serif;
      --body: '${theme.bodyFont}', sans-serif;
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: var(--body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 20px; }
    .btn { display: inline-block; padding: 10px 24px; border-radius: 8px; background: var(--cta); color: #fff; text-decoration: none; font-weight: 500; font-size: 14px; transition: opacity .2s; border: none; cursor: pointer; text-align: center; }
    .btn:hover { opacity: .9; }
    .section-title { font-family: var(--heading); font-size: 28px; font-weight: 700; color: var(--text); margin-bottom: 8px; }
    .section-subtitle { font-size: 15px; color: var(--text); opacity: 0.8; margin-top: 8px; margin-bottom: 0; max-width: 640px; margin-left: auto; margin-right: auto; line-height: 1.6; }
    .section-label { font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 2px; color: var(--accent); margin-bottom: 12px; display: block; }
    .card { background: #fff; border-radius: 12px; padding: 24px; border: 1px solid rgba(0,0,0,0.06); }
    input, textarea { font-family: var(--body); padding: 10px 12px; border-radius: 8px; border: 1px solid rgba(0,0,0,0.08); font-size: 14px; width: 100%; }
    input:focus, textarea:focus { outline: none; border-color: var(--accent); }
    
    /* Responsive utility classes */
    .grid-founder { display: grid; grid-template-columns: 240px 1fr; gap: 32px; align-items: start; }
    .grid-ps { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; background: rgba(255,255,255,0.05); border-radius: 12px; padding: 24px; text-align: left; }
    .grid-footer { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 32px; margin-bottom: 32px; }
    .hero-title { font-family: var(--heading); font-size: 40px; font-weight: 700; margin-bottom: 16px; line-height: 1.2; }
    .mobile-menu-btn { display: none; background: none; border: none; font-size: 24px; color: var(--text); cursor: pointer; }
    .nav-links { display: flex; align-items: center; gap: 28px; }
    
    .faq-item { cursor: pointer; }
    .faq-answer { display: none; margin-top: 10px; font-size: 13px; color: rgba(0,0,0,0.6); line-height: 1.6; }
    .faq-item.active .faq-answer { display: block; }
    
    @media (max-width: 768px) {
      .hero-title { font-size: 32px; }
      .section-title { font-size: 24px; }
      .grid-founder { grid-template-columns: 1fr; }
      .grid-ps { grid-template-columns: 1fr; }
      .mobile-menu-btn { display: block; }
      .nav-links { display: none; flex-direction: column; position: absolute; top: 64px; left: 0; right: 0; background: rgba(255,255,255,0.98); padding: 20px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); gap: 16px; align-items: stretch; border-bottom: 1px solid rgba(0,0,0,0.06); }
      .nav-links.active { display: flex; }
      .nav-links .btn { width: 100%; }
    }
  </style>
</head>
<body>
  ${navHTML}
  ${body}
  ${footerHTML}
  
  <div id="blog-modal" style="display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 9999; align-items: center; justify-content: center; padding: 20px;">
    <div style="background: #fff; width: 100%; max-width: 800px; max-height: 90vh; border-radius: 12px; overflow-y: auto; position: relative;">
      <button onclick="closeBlogModal()" style="position: absolute; top: 20px; right: 20px; background: rgba(0,0,0,0.05); width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: none; font-size: 20px; cursor: pointer; color: #333; z-index: 10;"><i class="ri-close-line"></i></button>
      <div style="padding: 48px; position: relative;">
        <span id="bm-date" style="font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--accent);"></span>
        <h2 id="bm-title" style="font-family: var(--heading); font-size: 36px; font-weight: 700; color: var(--text); margin: 16px 0 32px; line-height: 1.3;"></h2>
        <div id="bm-content" style="font-size: 16px; color: rgba(0,0,0,0.75); line-height: 1.8;"></div>
      </div>
    </div>
  </div>

  <script>
    // Mobile Menu Toggle
    const mobileMenuBtn = document.getElementById('mobile-menu-btn');
    const navLinks = document.getElementById('nav-links');
    if (mobileMenuBtn && navLinks) {
      mobileMenuBtn.addEventListener('click', () => {
        navLinks.classList.toggle('active');
        const icon = mobileMenuBtn.querySelector('i');
        if (navLinks.classList.contains('active')) {
          icon.classList.replace('ri-menu-line', 'ri-close-line');
        } else {
          icon.classList.replace('ri-close-line', 'ri-menu-line');
        }
      });
    }

    // FAQ Toggle
    const faqItems = document.querySelectorAll('.faq-item');
    faqItems.forEach(item => {
      item.addEventListener('click', () => {
        item.classList.toggle('active');
        const icon = item.querySelector('.faq-icon');
        if (icon) {
          if (item.classList.contains('active')) {
            icon.classList.replace('ri-add-line', 'ri-subtract-line');
          } else {
            icon.classList.replace('ri-subtract-line', 'ri-add-line');
          }
        }
      });
    });

    // Blog Modal Functions
    window.openBlogModal = function(el) {
      if (!el || !el.getAttribute('data-post')) return;
      try {
        const post = JSON.parse(decodeURIComponent(el.getAttribute('data-post')));
        document.getElementById('bm-date').textContent = post.date || '';
        document.getElementById('bm-title').textContent = post.title || '';
        document.getElementById('bm-content').innerHTML = post.content || post.excerpt || '';
        document.getElementById('blog-modal').style.display = 'flex';
        document.body.style.overflow = 'hidden';
      } catch (err) {
        console.error('Failed to parse blog post data', err);
      }
    };
    window.closeBlogModal = function() {
      document.getElementById('blog-modal').style.display = 'none';
      document.body.style.overflow = 'auto';
    };
    const blogModalEl = document.getElementById('blog-modal');
    if (blogModalEl) {
      blogModalEl.addEventListener('click', function(e) {
        if (e.target === this) window.closeBlogModal();
      });
    }
  </script>
  ${previewScript}
</body>
</html>`;
}

export function generateNavHTML(navPages: { id: string; name: string; enabled: boolean }[], theme: ThemeData, brandName: string, brandLogo: string | null, isPreview: boolean = false): string {
  return `<nav style="position: sticky; top: 0; z-index: 50; background: rgba(255,255,255,0.95); backdrop-filter: blur(8px); border-bottom: 1px solid rgba(0,0,0,0.06);">
    <div class="container" style="display: flex; align-items: center; justify-content: space-between; height: 64px;">
      <div style="display: flex; align-items: center; gap: 12px;">
        ${brandLogo ?
      `<a href="${isPreview ? '#' : 'index.html'}" ${isPreview ? 'data-nav-id="home"' : ''} style="display: flex; align-items: center; text-decoration: none;">
         <img src="${brandLogo}" alt="${brandName}" data-component="logo" style="height: 38px; max-height: 42px; width: auto; max-width: 200px; object-fit: contain; display: block;">
       </a>` :
      `<a href="${isPreview ? '#' : 'index.html'}" ${isPreview ? 'data-nav-id="home"' : ''} style="display: flex; align-items: center; gap: 10px; text-decoration: none;">
         <div style="width: 36px; height: 36px; border-radius: 8px; background: var(--primary); display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
           <span style="color: #fff; font-weight: 700; font-size: 13px;">${brandName.substring(0, 3).toUpperCase()}</span>
         </div>
         <span style="font-family: var(--heading); font-size: 18px; font-weight: 700; color: var(--text); line-height: 1.2;">${brandName}</span>
       </a>`
    }
      </div>
      <button id="mobile-menu-btn" class="mobile-menu-btn">
        <i class="ri-menu-line"></i>
      </button>
      <div id="nav-links" class="nav-links">
        ${navPages.filter(p => p.name.toLowerCase() !== 'contact').map(p => {
      const slug = p.name.toLowerCase().replace(/\s+/g, '-');
      const href = isPreview ? '#' : (slug === 'home' ? 'index.html' : `${slug}.html`);
      const navAttr = isPreview ? `data-nav-id="${p.id}"` : '';
      return `<a href="${href}" ${navAttr} style="font-size: 14px; font-weight: 500; color: var(--text); text-decoration: none; transition: opacity .2s;">${p.name}</a>`;
    }).join('')}
      </div>
    </div>
  </nav>`;
}

export function generateFooterHTML(navPages: { id: string; name: string; enabled: boolean }[], theme: ThemeData, brandName: string, brandLogo: string | null, globalContactInfo: GlobalContactInfo, isPreview: boolean = false): string {
  return `<footer style="background: var(--primary); padding: 48px 0 24px; margin-top: 0; color: #fff;">
    <div class="container">
      <div class="grid-footer">
        <div>
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 12px;">
            ${brandLogo ?
      `<a href="${isPreview ? '#' : 'index.html'}" ${isPreview ? 'data-nav-id="home"' : ''} style="display: inline-block; text-decoration: none;">
         <img src="${brandLogo}" alt="${brandName}" data-component="logo" style="height: 32px; max-height: 36px; width: auto; max-width: 160px; object-fit: contain; background: white; border-radius: 6px; padding: 4px; display: block;">
       </a>` :
      `<a href="${isPreview ? '#' : 'index.html'}" ${isPreview ? 'data-nav-id="home"' : ''} style="display: flex; align-items: center; gap: 8px; text-decoration: none;">
         <div style="width: 32px; height: 32px; border-radius: 8px; background: var(--accent); display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
           <span style="color: #fff; font-weight: 700; font-size: 12px;">${brandName.substring(0, 3).toUpperCase()}</span>
         </div>
         <span style="font-family: var(--heading); font-size: 15px; font-weight: 700; color: #fff;">${brandName}</span>
       </a>`
    }
          </div>
          <p style="font-size: 12px; color: rgba(255,255,255,0.5); line-height: 1.6;">${globalContactInfo?.description || 'Your trusted partner for growth and success.'}</p>
        </div>
        <div>
          <h4 style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: rgba(255,255,255,0.5); margin-bottom: 12px;">Pages</h4>
          <ul style="list-style: none;">
            ${navPages.map(p => {
      const slug = p.name.toLowerCase().replace(/\s+/g, '-');
      const href = isPreview ? '#' : (slug === 'home' ? 'index.html' : `${slug}.html`);
      const navAttr = isPreview ? `data-nav-id="${p.id}"` : '';
      return `<li style="margin-bottom: 6px;"><a href="${href}" ${navAttr} style="font-size: 13px; color: rgba(255,255,255,0.7); text-decoration: none;">${p.name}</a></li>`;
    }).join('')}
          </ul>
        </div>

        <div>
          <h4 style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: rgba(255,255,255,0.5); margin-bottom: 12px;">Legal</h4>
          <ul style="list-style: none;">
            <li style="margin-bottom: 6px;"><a href="javascript:void(0)" style="font-size: 13px; color: rgba(255,255,255,0.7); text-decoration: none;">Privacy Policy</a></li>
            <li style="margin-bottom: 6px;"><a href="javascript:void(0)" style="font-size: 13px; color: rgba(255,255,255,0.7); text-decoration: none;">Terms of Service</a></li>
          </ul>
        </div>
        <div>
          <h4 style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: rgba(255,255,255,0.5); margin-bottom: 12px;">Connect</h4>
          <ul style="list-style: none;">
            ${globalContactInfo?.email ? `<li style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px; font-size: 13px; color: rgba(255,255,255,0.7);"><i class="ri-mail-line" style="color: var(--accent);"></i> ${globalContactInfo.email}</li>` : ''}
            ${globalContactInfo?.phone ? `<li style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px; font-size: 13px; color: rgba(255,255,255,0.7);"><i class="ri-phone-line" style="color: var(--accent);"></i> ${globalContactInfo.phone}</li>` : ''}
            ${globalContactInfo?.location ? `<li style="margin-bottom: 8px; display: flex; align-items: flex-start; gap: 8px; font-size: 13px; color: rgba(255,255,255,0.7);"><i class="ri-map-pin-line" style="color: var(--accent); margin-top: 2px;"></i> ${globalContactInfo.location}</li>` : ''}
          </ul>
        </div>
      </div>
      <div style="display: flex; justify-content: center; gap: 16px; margin: 32px 0 24px;">
        ${globalContactInfo?.socials?.instagram ? `<a href="${globalContactInfo.socials.instagram}" target="_blank" style="color: rgba(255,255,255,0.6);"><i class="ri-instagram-line" style="font-size: 18px;"></i></a>` : ''}
        ${globalContactInfo?.socials?.twitter ? `<a href="${globalContactInfo.socials.twitter}" target="_blank" style="color: rgba(255,255,255,0.6);"><i class="ri-twitter-x-line" style="font-size: 18px;"></i></a>` : ''}
        ${globalContactInfo?.socials?.linkedin ? `<a href="${globalContactInfo.socials.linkedin}" target="_blank" style="color: rgba(255,255,255,0.6);"><i class="ri-linkedin-fill" style="font-size: 18px;"></i></a>` : ''}
        ${globalContactInfo?.socials?.facebook ? `<a href="${globalContactInfo.socials.facebook}" target="_blank" style="color: rgba(255,255,255,0.6);"><i class="ri-facebook-circle-fill" style="font-size: 18px;"></i></a>` : ''}
        ${globalContactInfo?.socials?.youtube ? `<a href="${globalContactInfo.socials.youtube}" target="_blank" style="color: rgba(255,255,255,0.6);"><i class="ri-youtube-fill" style="font-size: 18px;"></i></a>` : ''}
      </div>
      <div style="border-top: 1px solid rgba(255,255,255,0.08); padding-top: 20px; display: flex; justify-content: space-between; align-items: center;">
        <p style="font-size: 12px; color: rgba(255,255,255,0.4);">&copy; 2026 ${brandName}. All rights reserved.</p>
        <p style="font-size: 12px; color: rgba(255,255,255,0.4);">Powered by <span style="color: #fff;">Marketing4Sight</span></p>
      </div>
    </div>
  </footer>`;
}

function getSectionHeaderHTML(
  heading: string,
  subtitle?: string,
  options: {
    titleColor?: string;
    subColor?: string;
    isDark?: boolean;
    marginBottom?: string;
    headingTag?: 'h1' | 'h2' | 'h3';
    extraTitleStyles?: string;
  } = {}
): string {
  const cleanHeading = (heading || '').trim();
  const cleanSub = (subtitle || '').trim();

  // Guard: Only show subtitle if it is non-empty AND not identical to heading (case-insensitive)
  const showSub = cleanSub.length > 0 && cleanSub.toLowerCase() !== cleanHeading.toLowerCase();

  if (!cleanHeading && !showSub) return '';

  const mb = options.marginBottom || '40px';
  const tag = options.headingTag || 'h2';
  const titleColorStyle = options.titleColor ? `color: ${options.titleColor};` : '';
  const subColorStyle = options.subColor
    ? `color: ${options.subColor};`
    : (options.isDark ? 'color: rgba(255,255,255,0.85);' : `color: var(--text); opacity: 0.8;`);
  const extraTitle = options.extraTitleStyles || '';
  const titleMarginBottom = showSub ? 'margin-bottom: 8px;' : 'margin-bottom: 0;';

  return `
    <div style="text-align: center; margin-bottom: ${mb}; max-width: 720px; margin-left: auto; margin-right: auto;">
      ${cleanHeading ? `<${tag} class="section-title" style="${titleColorStyle} ${titleMarginBottom} ${extraTitle}">${cleanHeading}</${tag}>` : ''}
      ${showSub ? `<p class="section-subtitle" style="${subColorStyle}">${cleanSub}</p>` : ''}
    </div>
  `;
}

export function generateSectionHTML(section: ContentSection, theme: ThemeData, allPages: PageContent[], globalContactInfo: GlobalContactInfo, isPreview: boolean = false, pageId?: string): string {
  switch (section.type) {
    case 'hero': {
      const slides = Array.isArray(section.content) ? section.content : [];
      const slide = parseHeroSlide(slides[0] || {});
      return `<section style="position: relative; height: 560px; overflow: hidden; display: flex; align-items: center; justify-content: center; background: var(--primary);">
        ${slide.image ? `<img src="${slide.image}" alt="" data-component="hero" style="position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;"><div style="position: absolute; inset: 0; background: rgba(0,0,0,0.4);"></div>` : ''}
        <div class="container" style="position: relative; z-index: 2; text-align: center; color: #fff;">
          <h1 class="hero-title">${slide.headline}</h1>
          <p style="font-size: 18px; color: rgba(255,255,255,0.8); margin-bottom: 28px; max-width: 560px; margin-left: auto; margin-right: auto;">${slide.sub}</p>
          <a href="${isPreview ? 'javascript:void(0)' : '#'}" class="btn">${slide.cta}</a>
        </div>
      </section>`;
    }

    case 'usp': {
      const cards = Array.isArray(section.content) ? section.content : [];
      const parsed = cards.map(c => parseJSONSafely(c, { title: typeof c === 'string' ? c : '', description: '', icon: 'ri-star-line' }));
      const heading = section.heading || (section.title && section.title !== 'Brand USP Strip' && section.title !== 'usp' ? section.title : 'Why Choose Us');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);
      return `<section style="padding: 72px 0; background: var(--bg);">
        <div class="container">
          ${headerHTML}
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 32px;">
            ${parsed.map(card => {
        const iconClass = card.icon?.trim() ? card.icon : 'ri-star-line';
        return `<div style="text-align: center;">
              <div style="width: 56px; height: 56px; margin: 0 auto 16px; border-radius: 12px; background: ${theme.accent}15; display: flex; align-items: center; justify-content: center;">
                <i class="${iconClass}" style="font-size: 22px; color: var(--accent);"></i>
              </div>
              <h3 style="font-family: var(--heading); font-size: 17px; font-weight: 700; margin-bottom: 8px; color: var(--text);">${card.title}</h3>
              <p style="font-size: 14px; color: ${theme.text}99; line-height: 1.6;">${card.description}</p>
            </div>`;
      }).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'story': {
      const text = typeof section.content === 'string' ? section.content : '';
      const heading = section.heading || (section.title && section.title !== 'Brand Story Snippet' && section.title !== 'story' ? section.title : 'Our Story');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle, { titleColor: '#fff', isDark: true, marginBottom: '24px' });
      return `<section style="padding: 72px 0; background: var(--primary); color: #fff;">
        <div class="container" style="max-width: 640px; text-align: center;">
          ${headerHTML}
          <p style="font-size: 15px; color: rgba(255,255,255,0.7); line-height: 1.7;">${text}</p>
        </div>
      </section>`;
    }

    case 'grid': {
      const cards = Array.isArray(section.content) ? section.content : [];
      const parsed = cards.map(c => parseJSONSafely(c, { title: typeof c === 'string' ? c : '', description: '', icon: 'ri-briefcase-line' }));
      const heading = section.heading || (section.title && section.title !== 'grid' && section.title !== 'Differentiators Grid' ? section.title : 'Our Core Strengths');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);
      return `<section style="padding: 72px 0; background: var(--bg);">
        <div class="container">
          ${headerHTML}
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px;">
            ${parsed.map(card => {
        const iconClass = card.icon?.trim() ? card.icon : 'ri-briefcase-line';
        return `<div class="card">
              <div style="width: 44px; height: 44px; border-radius: 10px; background: ${theme.accent}12; display: flex; align-items: center; justify-content: center; margin-bottom: 14px;">
                <i class="${iconClass}" style="font-size: 18px; color: var(--accent);"></i>
              </div>
              <h3 style="font-size: 15px; font-weight: 600; margin-bottom: 6px; color: var(--text);">${card.title}</h3>
              <p style="font-size: 13px; color: ${theme.text}99; line-height: 1.6;">${card.description}</p>
            </div>`;
      }).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'gallery': {
      const isHomeGallery = pageId === 'home';
      const galleryPage = allPages.find(p => p.pageId === 'gallery');
      const galleryPageSection = galleryPage?.sections.find(s => s.type === 'gallery');
      const galleryItems = Array.isArray(galleryPageSection?.content) ? galleryPageSection.content : [];
      if (isHomeGallery && galleryItems.length === 0) return '';
      const rawItems = isHomeGallery
        ? galleryItems
        : (Array.isArray(section.content) ? section.content : []);
      const items = isHomeGallery ? rawItems.slice(0, 3) : rawItems;
      const parsed = items.map(item => parseJSONSafely(item, { src: item, type: 'image' }));
      const images = parsed.filter(i => i.type === 'image' && i.src && !i.src.includes('placeholder'));
      const heading = section.heading || (section.title && section.title !== 'Gallery Strip' && section.title !== 'gallery' && section.title !== 'Image Gallery' && section.title !== 'Visual Showcase' ? section.title : 'Our Work in Action');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);
      return `<section style="padding: 72px 0; background: var(--bg);">
        <div class="container">
          ${headerHTML}
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px;">
            ${images.map(img => `<div style="aspect-ratio: 4/3; border-radius: 12px; overflow: hidden; position: relative;">
              <img src="${img.src}" alt="${img.title || 'Gallery'}" data-component="gallery" style="width: 100%; height: 100%; object-fit: cover;">
              <span style="position: absolute; top: 12px; left: 12px; background: rgba(0,0,0,0.6); color: white; padding: 4px 10px; border-radius: 20px; font-size: 10px; backdrop-filter: blur(4px); z-index: 10; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">${img.category === 'Behind the Scenes' ? 'BTS' : (img.category || 'Services')}</span>
            </div>`).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'blog': {
      const isHomeBlog = pageId === 'home';
      const blogPage = allPages.find(p => p.pageId === 'blog');
      const blogPageSection = blogPage?.sections.find(s => s.type === 'blog');
      const blogItems = Array.isArray(blogPageSection?.content) ? blogPageSection.content : [];
      if (isHomeBlog && blogItems.length < 3) return '';
      const rawPosts = isHomeBlog
        ? blogItems
        : (Array.isArray(section.content) ? section.content : []);
      const posts = isHomeBlog ? rawPosts.slice(0, 3) : rawPosts;
      const parsed = posts.map(p => parseJSONSafely(p, { title: p, image: '', date: '', content: '' }));
      const heading = section.heading || (section.title && section.title !== 'Top 3 Blogs' && section.title !== 'blog' && section.title !== 'Blog Articles' ? section.title : 'Latest Insights & Articles');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);
      return `<section style="padding: 72px 0; background: ${theme.primary}08;">
        <div class="container">
          ${headerHTML}
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px;">
            ${parsed.map((post) => `<div class="card" style="overflow: hidden; padding: 0; cursor: pointer; transition: transform 0.2s;" onclick="openBlogModal(this)" data-post="${encodeURIComponent(JSON.stringify(post)).replace(/"/g, '&quot;')}" onmouseover="this.style.transform='translateY(-4px)'" onmouseout="this.style.transform='translateY(0)'">
              <div style="height: 160px; background: ${theme.primary}0a; display: flex; align-items: center; justify-content: center;">
                ${post.image ? `<img src="${post.image}" alt="${post.title}" style="width: 100%; height: 100%; object-fit: cover;" />` : `<i class="ri-article-line" style="font-size: 36px; color: ${theme.primary}33;"></i>`}
              </div>
              <div style="padding: 20px;">
                <span style="font-size: 10px; font-weight: 500; text-transform: uppercase; letter-spacing: 1px; color: var(--accent);">${post.date}</span>
                <h3 style="font-size: 15px; font-weight: 600; margin: 6px 0 8px; color: var(--text);">${post.title}</h3>
                <p style="font-size: 13px; color: ${theme.text}99; line-height: 1.5;">${generateExcerpt(post.content || '')}</p>
              </div>
            </div>`).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'contact':
    case 'contactInfo': {
      // The Contact Info section acts as a global editor in the builder
      // but should NOT render visually on the page. The data is consumed globally by the footer.
      return '';
    }

    case 'contactForm': {
      let data = parseJSONSafely(section.content, {});
      const location = globalContactInfo?.location;
      const heading = section.heading || (section.title && section.title !== 'Contact Form' && section.title !== 'contactForm' && section.title !== 'Get in Touch' ? section.title : (data.heading || 'Send a Message'));
      const subtitle = section.subtitle || section.description || data.subtext || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle, { marginBottom: '32px' });
      const mapHtml = location ? `<div style="margin-top: 32px; border-radius: 8px; overflow: hidden; height: 300px;">
          <iframe width="100%" height="100%" frameborder="0" scrolling="no" marginheight="0" marginwidth="0" 
          src="https://maps.google.com/maps?q=${encodeURIComponent(location)}&output=embed"></iframe>
        </div>` : '';
        
      return `<section style="padding: 72px 0; background: var(--bg);">
        <div class="container" style="max-width: 640px;">
          ${headerHTML}
          <form style="display: flex; flex-direction: column; gap: 16px;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <input type="text" placeholder="Name" style="padding: 10px 14px; border: 1px solid ${theme.text}15; border-radius: 8px; font-size: 14px;">
              <input type="email" placeholder="Email" style="padding: 10px 14px; border: 1px solid ${theme.text}15; border-radius: 8px; font-size: 14px;">
              <input type="text" placeholder="Phone" style="padding: 10px 14px; border: 1px solid ${theme.text}15; border-radius: 8px; font-size: 14px;">
              <input type="text" placeholder="Company" style="padding: 10px 14px; border: 1px solid ${theme.text}15; border-radius: 8px; font-size: 14px;">
            </div>
            <textarea placeholder="Message" rows="4" style="padding: 10px 14px; border: 1px solid ${theme.text}15; border-radius: 8px; font-size: 14px; resize: none;"></textarea>
            <button type="submit" style="background: var(--cta); color: #fff; padding: 12px 24px; border: none; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer;">Send Message</button>
          </form>
          ${mapHtml}
        </div>
      </section>`;
    }

    case 'founder': {
      const members = Array.isArray(section.content) ? section.content : [];
      const parsed = members.map(m => parseJSONSafely(m, { name: m, role: '', bio: '', image: '' }));
      const f = parsed[0] || {};
      const heading = section.heading || (section.title && section.title !== 'Founder' && section.title !== 'founder' && section.title !== 'Founder Profile' && section.title !== 'Meet the Founder' ? section.title : 'Meet the Founder');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);
      return `<section style="padding: 72px 0; background: var(--bg);">
        <div class="container">
          ${headerHTML}
          <div class="grid-founder">
            <div style="aspect-ratio: 4/5; border-radius: 12px; overflow: hidden; background: #f0f0f0;">
              ${f.image ? `<img src="${f.image}" alt="${f.name}" data-component="founder" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #ccc;"><i class="ri-user-line" style="font-size: 48px;"></i></div>'}
            </div>
            <div>
              <h3 style="font-family: var(--heading); font-size: 20px; font-weight: 700; color: var(--text); margin-bottom: 4px;">${f.name}</h3>
              <p style="font-size: 14px; color: var(--accent); margin-bottom: 12px;">${f.role}</p>
              <p style="font-size: 14px; color: ${theme.text}99; line-height: 1.7;">${f.bio}</p>
            </div>
          </div>
        </div>
      </section>`;
    }

    case 'team': {
      const members = Array.isArray(section.content) ? section.content : [];
      const parsed = members.map(m => parseJSONSafely(m, { name: m, role: '', bio: '', image: '' }));
      const heading = section.heading || (section.title && section.title !== 'Team' && section.title !== 'team' && section.title !== 'Team Members' && section.title !== 'Our Leadership Team' ? section.title : 'Meet the Team');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);
      return `<section style="padding: 72px 0; background: var(--bg);">
        <div class="container">
          ${headerHTML}
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px;">
            ${parsed.map(m => `<div class="card" style="text-align: center;">
              <div style="aspect-ratio: 1; border-radius: 10px; overflow: hidden; background: #f0f0f0; margin-bottom: 14px;">
                ${m.image ? `<img src="${m.image}" alt="${m.name}" data-component="team" style="width: 100%; height: 100%; object-fit: cover;">` : '<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #ccc;"><i class="ri-user-line" style="font-size: 36px;"></i></div>'}
              </div>
              <h4 style="font-size: 15px; font-weight: 600; color: var(--text); margin-bottom: 2px;">${m.name}</h4>
              <p style="font-size: 12px; color: var(--accent); margin-bottom: 8px;">${m.role}</p>
              <p style="font-size: 12px; color: ${theme.text}99; line-height: 1.5;">${m.bio}</p>
            </div>`).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'testimonial': {
      const items = Array.isArray(section.content) ? section.content : [];
      const parsed = items.map(i => parseJSONSafely(i, { quote: i, client: '', role: '' }));
      const heading = section.heading || (section.title && section.title !== 'Client Testimonials' && section.title !== 'testimonial' ? section.title : 'What Our Clients Say');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);
      return `<section style="padding: 72px 0; background: ${theme.primary}08;">
        <div class="container">
          ${headerHTML}
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px;">
            ${parsed.map(t => `<div class="card" style="position: relative;">
              <i class="ri-double-quotes-l" style="font-size: 32px; color: ${theme.accent}33; position: absolute; top: 16px; right: 16px;"></i>
              <p style="font-size: 14px; color: var(--text); line-height: 1.7; margin-bottom: 16px; font-style: italic;">${t.quote}</p>
              <div style="display: flex; align-items: center; gap: 10px;">
                <div style="width: 36px; height: 36px; border-radius: 50%; background: ${theme.accent}20; display: flex; align-items: center; justify-content: center;">
                  <i class="ri-user-line" style="font-size: 16px; color: var(--accent);"></i>
                </div>
                <div>
                  <p style="font-size: 13px; font-weight: 600; color: var(--text);">${t.client}</p>
                  <p style="font-size: 11px; color: ${theme.text}99;">${t.role}</p>
                </div>
              </div>
            </div>`).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'faq': {
      const items = Array.isArray(section.content) ? section.content : [];
      const parsed = items.map(i => parseJSONSafely(i, { question: i, answer: '' }));
      const heading = section.heading || (section.title && section.title !== 'Frequently Asked Questions' && section.title !== 'faq' && section.title !== 'FAQ Accordion' ? section.title : 'Frequently Asked Questions');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);
      return `<section style="padding: 72px 0; background: var(--bg);">
        <div class="container" style="max-width: 760px;">
          ${headerHTML}
          <div style="display: flex; flex-direction: column; gap: 12px;">
            ${parsed.map((faq, idx) => `<div class="card faq-item" style="padding: 20px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <h4 style="font-size: 14px; font-weight: 600; color: var(--text);">${idx + 1}. ${faq.question}</h4>
                <i class="ri-add-line faq-icon" style="color: var(--accent);"></i>
              </div>
              <p class="faq-answer">${faq.answer}</p>
            </div>`).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'clientLogos': {
      const items = Array.isArray(section.content) ? section.content : [];
      const parsed = items.map(i => parseJSONSafely(i, { name: i, logo: '' }));
      const heading = section.heading || (section.title && section.title !== 'Our Clients' && section.title !== 'clientLogos' && section.title !== 'Client & Partner Logos' ? section.title : 'Trusted by Industry Leaders');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle, { marginBottom: '24px' });
      return `<section style="padding: 48px 0; background: var(--bg); border-top: 1px solid rgba(0,0,0,0.04); border-bottom: 1px solid rgba(0,0,0,0.04);">
        <div class="container">
          ${headerHTML}
          <div style="display: flex; align-items: center; justify-content: center; gap: 32px; flex-wrap: wrap;">
            ${parsed.filter(l => l.logo && !l.logo.includes('placeholder')).map(l => `<img src="${l.logo}" alt="${l.name}" data-component="clientLogo" style="height: 36px; opacity: 0.7;">`).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'problemSolution': {
      const items = Array.isArray(section.content) ? section.content : [];
      let parsed: { headline?: string; problems?: { pain: string; solution: string }[] } = {};
      try { parsed = JSON.parse(items[0] || '{}'); } catch { /* noop */ }
      const problems = parsed.problems || [];
      const heading = section.heading || (section.title && section.title !== 'Problem-Solution' && section.title !== 'problemSolution' && section.title !== 'Problem & Solution' ? section.title : (parsed.headline || 'How We Transform Your Challenges'));
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle, { titleColor: '#fff', isDark: true, marginBottom: '40px', extraTitleStyles: 'font-size: 36px;' });
      return `<section style="padding: 72px 0; background: var(--primary); color: #fff;">
        <div class="container" style="max-width: 800px; text-align: center;">
          ${headerHTML}
          <div style="display: flex; flex-direction: column; gap: 24px;">
            ${problems.map(p => `<div class="grid-ps">
              <div>
                <p style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #ff6b6b; margin-bottom: 6px;">Challenge</p>
                <p style="font-size: 14px; color: rgba(255,255,255,0.7);">${p.pain}</p>
              </div>
              <div>
                <p style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #51cf66; margin-bottom: 6px;">Solution</p>
                <p style="font-size: 14px; color: rgba(255,255,255,0.9);">${p.solution}</p>
              </div>
            </div>`).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'serviceCard':
    case 'productCard': {
      const items = Array.isArray(section.content) ? section.content : [];
      const parsed = items.map(i => parseJSONSafely(i, { name: i, description: '', icon: 'ri-briefcase-line', image: '', url: '#' }));
      const isProduct = section.type === 'productCard';
      const defaultTitle = isProduct ? 'Featured Products & Solutions' : 'Services Designed to Scale';
      const heading = section.heading || (section.title && section.title !== 'Our Services' && section.title !== 'Our Products' && section.title !== 'serviceCard' && section.title !== 'productCard' && section.title !== 'Service Cards' && section.title !== 'Product Cards' ? section.title : defaultTitle);
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);
      return `<section style="padding: 72px 0; background: var(--bg);">
        <div class="container">
          ${headerHTML}
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px;">
            ${parsed.map(c => {
        const iconClass = c.icon?.trim() ? c.icon : 'ri-briefcase-line';
        return `<div class="card">
              ${isProduct ? `
                <div style="width: 100%; height: 160px; border-radius: 10px; background: #f3f4f6; margin-bottom: 14px; overflow: hidden; display: flex; align-items: center; justify-content: center;">
                  ${c.image ? `<img src="${c.image}" alt="${c.name}" style="width: 100%; height: 100%; object-fit: cover;" />` : `<i class="ri-image-line" style="font-size: 32px; color: #d1d5db;"></i>`}
                </div>
              ` : `
                <div style="width: 44px; height: 44px; border-radius: 10px; background: ${theme.accent}12; display: flex; align-items: center; justify-content: center; margin-bottom: 14px;">
                  <i class="${iconClass}" style="font-size: 18px; color: var(--accent);"></i>
                </div>
              `}
              <h3 style="font-size: 15px; font-weight: 600; margin-bottom: 6px; color: var(--text);">${c.name}</h3>
              <p style="font-size: 13px; color: ${theme.text}99; line-height: 1.6; margin-bottom: 14px;">${c.description}</p>
              <a href="${isPreview ? 'javascript:void(0)' : (c.url || '#')}" style="font-size: 13px; font-weight: 500; color: var(--accent); text-decoration: none;">Learn more &rarr;</a>
            </div>`;
      }).join('')}
          </div>
        </div>
      </section>`;
    }

    case 'custom':
    case 'customSection': {
      const payload = normalizeCustomSection(section.content);
      const { layout, blocks } = payload;

      const heading = section.heading || (section.title && section.title !== 'Custom Section' && section.title !== 'custom' && section.title !== 'New Custom Section' ? section.title : 'Custom Highlights');
      const subtitle = section.subtitle || section.description || '';
      const headerHTML = getSectionHeaderHTML(heading, subtitle);

      const displayBlocks = blocks && blocks.length > 0 ? blocks : [createDefaultBlock(1)];

      let layoutHTML = '';

      if (layout === 'cardGrid') {
        layoutHTML = `
          <div style="max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 24px;">
            ${displayBlocks.map(b => {
              const hasImage = (b.mediaType === 'image' || (!b.mediaType && b.image)) && b.image;
              const hasIcon = b.mediaType === 'icon' || (!b.mediaType && !hasImage && b.mediaType !== 'none');
              return `
              <div class="card" style="padding: 24px; border-radius: 12px; background: #ffffff; border: 1px solid ${theme.text}15; display: flex; flex-direction: column; transition: transform 0.2s, box-shadow 0.2s;" onmouseover="this.style.transform='translateY(-4px)';this.style.boxShadow='0 10px 24px rgba(0,0,0,0.06)'" onmouseout="this.style.transform='translateY(0)';this.style.boxShadow='none'">
                ${hasImage ? `
                  <div style="aspect-ratio: 16/10; border-radius: 8px; overflow: hidden; margin-bottom: 16px; background: ${theme.primary}0a;">
                    <img src="${b.image}" alt="${b.title || heading}" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
                  </div>
                ` : hasIcon ? `
                  <div style="width: 48px; height: 48px; border-radius: 10px; background: ${theme.accent}15; display: flex; align-items: center; justify-content: center; margin-bottom: 16px;">
                    ${b.iconType === 'custom' && b.customIcon ? `
                      <img src="${b.customIcon}" alt="${b.title || 'Icon'}" style="width: 24px; height: 24px; object-fit: contain; display: block;" />
                    ` : `
                      <i class="${b.icon || 'ri-star-line'}" style="font-size: 22px; color: var(--accent);"></i>
                    `}
                  </div>
                ` : ''}
                ${b.title ? `<h3 style="font-size: 17px; font-weight: 600; margin-bottom: 8px; color: var(--text);">${b.title}</h3>` : ''}
                <div style="font-size: 14px; color: ${theme.text}99; line-height: 1.6; flex: 1;">
                  ${b.body ? (b.body.includes('<p>') ? b.body : `<p>${b.body}</p>`) : ''}
                </div>
              </div>`;
            }).join('')}
          </div>
        `;
      } else if (layout === 'alternating') {
        layoutHTML = `
          <div style="max-width: 960px; margin: 0 auto; display: flex; flex-direction: column; gap: 32px;">
            ${displayBlocks.map((b, idx) => {
              const isEven = idx % 2 === 1;
              const hasImage = (b.mediaType === 'image' || (!b.mediaType && b.image)) && b.image;
              const hasIcon = b.mediaType === 'icon' || (!b.mediaType && !hasImage && b.mediaType !== 'none');
              
              if (hasImage) {
                return `
                  <div style="display: flex; flex-direction: ${isEven ? 'row-reverse' : 'row'}; gap: 36px; align-items: center; justify-content: space-between; flex-wrap: wrap;">
                    <div style="flex: 1; min-width: 280px; max-width: 440px;">
                      <div style="aspect-ratio: 4/3; border-radius: 14px; overflow: hidden; box-shadow: 0 6px 20px rgba(0,0,0,0.08);">
                        <img src="${b.image}" alt="${b.title || heading}" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
                      </div>
                    </div>
                    <div style="flex: 1; min-width: 280px;">
                      ${b.title ? `<h3 style="font-size: 20px; font-weight: 700; margin-bottom: 12px; color: var(--text);">${b.title}</h3>` : ''}
                      <div style="font-size: 15px; color: ${theme.text}dd; line-height: 1.8;">
                        ${b.body ? (b.body.includes('<p>') ? b.body : `<p>${b.body}</p>`) : ''}
                      </div>
                    </div>
                  </div>
                `;
              }

              if (hasIcon) {
                return `
                  <div style="background: #ffffff; border: 1px solid ${theme.text}15; border-radius: 16px; padding: 28px 32px; display: flex; flex-direction: ${isEven ? 'row-reverse' : 'row'}; gap: 24px; align-items: flex-start; box-shadow: 0 4px 16px rgba(0,0,0,0.03);">
                    <div style="width: 56px; height: 56px; border-radius: 14px; background: ${theme.accent}15; border: 1px solid ${theme.accent}30; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 4px 12px ${theme.accent}18;">
                      ${b.iconType === 'custom' && b.customIcon ? `
                        <img src="${b.customIcon}" alt="${b.title || 'Icon'}" style="width: 28px; height: 28px; object-fit: contain; display: block;" />
                      ` : `
                        <i class="${b.icon || 'ri-star-line'}" style="font-size: 28px; color: var(--accent);"></i>
                      `}
                    </div>
                    <div style="flex: 1; min-width: 0;">
                      ${b.title ? `<h3 style="font-size: 18px; font-weight: 700; margin-bottom: 10px; color: var(--text);">${b.title}</h3>` : ''}
                      <div style="font-size: 15px; color: ${theme.text}dd; line-height: 1.8;">
                        ${b.body ? (b.body.includes('<p>') ? b.body : `<p>${b.body}</p>`) : ''}
                      </div>
                    </div>
                  </div>
                `;
              }

              return `
                <div style="background: #ffffff; border: 1px solid ${theme.text}15; border-radius: 16px; padding: 28px 32px; box-shadow: 0 4px 16px rgba(0,0,0,0.03);">
                  ${b.title ? `<h3 style="font-size: 18px; font-weight: 700; margin-bottom: 10px; color: var(--text);">${b.title}</h3>` : ''}
                  <div style="font-size: 15px; color: ${theme.text}dd; line-height: 1.8;">
                    ${b.body ? (b.body.includes('<p>') ? b.body : `<p>${b.body}</p>`) : ''}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        `;
      } else {
        // Layout: 'story' (Narrative / Journey Timeline Flow)
        if (displayBlocks.length > 1) {
          // Multi-block Story Narrative Timeline Layout
          layoutHTML = `
            <div style="max-width: 860px; margin: 0 auto; position: relative; padding: 8px 0 8px 16px;">
              <!-- Connecting Story Timeline Spine -->
              <div style="position: absolute; top: 36px; bottom: 44px; left: 45px; width: 2px; background: linear-gradient(180deg, ${theme.accent} 0%, ${theme.primary}60 50%, ${theme.accent}20 100%); z-index: 0;"></div>

              <div style="display: flex; flex-direction: column; gap: 32px; position: relative; z-index: 1;">
                ${displayBlocks.map((b) => {
                  const hasImage = (b.mediaType === 'image' || (!b.mediaType && b.image)) && b.image;
                  const hasIcon = b.mediaType === 'icon' || (!b.mediaType && !hasImage && b.mediaType !== 'none');

                  return `
                    <div style="display: flex; gap: 24px; align-items: flex-start;">
                      <!-- Timeline Node / Icon Badge -->
                      <div style="flex-shrink: 0; width: 60px; height: 60px; border-radius: 18px; background: #ffffff; border: 2px solid ${theme.accent}40; display: flex; align-items: center; justify-content: center; box-shadow: 0 6px 20px ${theme.accent}22; position: relative; z-index: 2;">
                        ${hasIcon ? (
                          b.iconType === 'custom' && b.customIcon ? `
                            <img src="${b.customIcon}" alt="${b.title || 'Icon'}" style="width: 30px; height: 30px; object-fit: contain; display: block;" />
                          ` : `
                            <i class="${b.icon || 'ri-star-line'}" style="font-size: 28px; color: var(--accent);"></i>
                          `
                        ) : hasImage ? `
                          <i class="ri-image-2-line" style="font-size: 26px; color: var(--accent);"></i>
                        ` : `
                          <i class="ri-checkbox-circle-line" style="font-size: 26px; color: var(--accent);"></i>
                        `}
                      </div>

                      <!-- Story Narrative Content Box -->
                      <div style="flex: 1; min-width: 0; background: #ffffff; border: 1px solid ${theme.text}14; border-radius: 18px; padding: 26px 30px; box-shadow: 0 4px 18px rgba(0,0,0,0.03); transition: transform 0.2s, box-shadow 0.2s;" onmouseover="this.style.transform='translateX(4px)';this.style.boxShadow='0 8px 24px rgba(0,0,0,0.06)'" onmouseout="this.style.transform='translateX(0)';this.style.boxShadow='0 4px 18px rgba(0,0,0,0.03)'">
                        ${b.title ? `<h3 style="font-size: 19px; font-weight: 700; margin-bottom: 10px; color: var(--text);">${b.title}</h3>` : ''}
                        ${hasImage ? `
                          <div style="aspect-ratio: 16/9; border-radius: 12px; overflow: hidden; margin-bottom: 16px; background: ${theme.primary}0a;">
                            <img src="${b.image}" alt="${b.title || heading}" style="width: 100%; height: 100%; object-fit: cover; display: block;" />
                          </div>
                        ` : ''}
                        <div style="font-size: 14px; color: ${theme.text}dd; line-height: 1.75;">
                          ${b.body ? (b.body.includes('<p>') ? b.body : `<p>${b.body}</p>`) : ''}
                        </div>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        } else {
          // Single-block Story Spotlight Layout
          const first = displayBlocks[0] || { title: '', body: '', image: '', icon: 'ri-star-line', iconType: 'preset', customIcon: '' };
          const hasImage = (first.mediaType === 'image' || (!first.mediaType && first.image)) && first.image;
          const hasIcon = first.mediaType === 'icon' || (!first.mediaType && !hasImage && first.mediaType !== 'none');

          if (hasImage) {
            layoutHTML = `
              <div style="max-width: 960px; margin: 0 auto; display: flex; flex-direction: row; gap: 40px; align-items: center; justify-content: center; flex-wrap: wrap;">
                <div style="flex: 1; min-width: 280px; max-width: 440px; aspect-ratio: 4/3; border-radius: 14px; overflow: hidden; box-shadow: 0 8px 24px rgba(0,0,0,0.08);">
                  <img src="${first.image}" alt="${first.title || heading}" style="width: 100%; height: 100%; display: block; object-fit: cover;" />
                </div>
                <div style="flex: 1; min-width: 280px; font-size: 15px; color: ${theme.text}dd; line-height: 1.8;">
                  ${first.title ? `<h3 style="font-size: 24px; font-weight: 700; margin-bottom: 14px; color: var(--text);">${first.title}</h3>` : ''}
                  ${first.body ? (first.body.includes('<p>') ? first.body : `<p>${first.body}</p>`) : '<p>Custom section content will appear here.</p>'}
                </div>
              </div>
            `;
          } else if (hasIcon) {
            layoutHTML = `
              <div style="max-width: 780px; margin: 0 auto; background: #ffffff; border: 1px solid ${theme.text}15; border-radius: 20px; padding: 44px 36px; text-align: center; box-shadow: 0 8px 24px rgba(0,0,0,0.04);">
                <div style="width: 68px; height: 68px; border-radius: 20px; background: ${theme.accent}15; border: 1px solid ${theme.accent}30; margin: 0 auto 20px auto; display: inline-flex; align-items: center; justify-content: center; box-shadow: 0 6px 18px ${theme.accent}20;">
                  ${first.iconType === 'custom' && first.customIcon ? `
                    <img src="${first.customIcon}" alt="${first.title || 'Icon'}" style="width: 34px; height: 34px; object-fit: contain; display: block;" />
                  ` : `
                    <i class="${first.icon || 'ri-star-line'}" style="font-size: 32px; color: var(--accent);"></i>
                  `}
                </div>
                ${first.title ? `<h3 style="font-size: 24px; font-weight: 700; margin-bottom: 14px; color: var(--text);">${first.title}</h3>` : ''}
                <div style="font-size: 15px; color: ${theme.text}dd; line-height: 1.8; max-width: 640px; margin: 0 auto;">
                  ${first.body ? (first.body.includes('<p>') ? first.body : `<p>${first.body}</p>`) : '<p>Custom section content will appear here.</p>'}
                </div>
              </div>
            `;
          } else {
            layoutHTML = `
              <div style="max-width: 780px; margin: 0 auto; text-align: center;">
                ${first.title ? `<h3 style="font-size: 24px; font-weight: 700; margin-bottom: 14px; color: var(--text);">${first.title}</h3>` : ''}
                <div style="font-size: 15px; color: ${theme.text}dd; line-height: 1.8; max-width: 640px; margin: 0 auto;">
                  ${first.body ? (first.body.includes('<p>') ? first.body : `<p>${first.body}</p>`) : '<p>Custom section content will appear here.</p>'}
                </div>
              </div>
            `;
          }
        }
      }

      return `<section style="padding: 72px 0; background: var(--bg);">
        <div class="container">
          ${headerHTML}
          ${layoutHTML}
        </div>
      </section>`;
    }

    default:
      return '';
  }
}
